import { ILiderboard } from "../types/requests-interfaces";
import {
  IPromocode,
  IUser,
  IUserActiveSubscription,
  IUserBalance,
  IUserProfile,
  IUserRefLink,
  IUserStats,
} from "../types/user-interface";

const apiUrl = process.env.REACT_APP_API_URL;

export type SupportMessage = {
  id: number;
  sender: "user" | "operator";
  text: string;
  media_url?: string | null;
  media_type?: string | null;
  media_name?: string | null;
  created_at: string;
};

const parseJsonOrThrow = async <T,>(res: Response, message: string): Promise<T> => {
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(message);
  }
  return data as T;
};

export const getTopUsers = async (page: number = 0): Promise<ILiderboard> => {
  const res = await fetch(`${apiUrl}/user/leaderboard?limit=5&page=${page}`);
  return parseJsonOrThrow<ILiderboard>(res, "Не удалось загрузить лидерборд");
};

export const getUserLiderboard = async (userId: number): Promise<IUser> => {
  const res = await fetch(`${apiUrl}/user/leaderboard/${userId}`);
  return parseJsonOrThrow<IUser>(res, "Не удалось загрузить место пользователя");
};

export const getUserLiderboardByTg = async (userId: number): Promise<IUser> => {
  if (!Number.isFinite(userId) || userId <= 0) {
    throw new Error("Не указан пользователь Telegram");
  }
  const res = await fetch(`${apiUrl}/user/leaderboard/tg/${userId}`);
  return parseJsonOrThrow<IUser>(res, "Не удалось загрузить место пользователя");
};

export const getUserStats = async (userId: number): Promise<IUserStats> => {
  const res = await fetch(`${apiUrl}/user/invited-statistics?user_id=${userId}`);
  const user = await res.json();
  return user;
};

export const getUserRefLink = async (userId: number): Promise<string> => {
  if (!Number.isFinite(userId) || userId <= 0) return "";

  const res = await fetch(`${apiUrl}/user/users/${userId}/referral-link`);
  if (!res.ok) return "";

  const users: IUserRefLink = await res.json();
  return users.referral_link || "";
};

export const getUserProfile = async (userId: number): Promise<IUserProfile | null> => {
  if (!Number.isFinite(userId) || userId <= 0) return null;

  const res = await fetch(`${apiUrl}/user/users/${userId}/profile`);
  if (!res.ok) return null;

  return res.json();
};

export const updateTradingViewName = async (userId: number, tradingViewName: string): Promise<IUserProfile | null> => {
  if (!Number.isFinite(userId) || userId <= 0) {
    throw new Error("Не указан пользователь Telegram");
  }

  const res = await fetch(`${apiUrl}/user/users/${userId}/trading-view-name`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ trading_view_name: tradingViewName }),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail;
    throw new Error(typeof detail === "string" ? detail : "Не удалось сохранить никнейм");
  }

  return data as IUserProfile | null;
};

export const getUserActiveSubscriptions = async (userId: number): Promise<IUserActiveSubscription[]> => {
  if (!Number.isFinite(userId) || userId <= 0) return [];

  const res = await fetch(`${apiUrl}/user/${userId}/active-subscriptions`);
  if (!res.ok) return [];

  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
};

export const getUserBalance = async (userId: number): Promise<number> => {
  if (!Number.isFinite(userId) || userId <= 0) return 0;

  const res = await fetch(`${apiUrl}/user/users/${userId}/balance`);
  if (!res.ok) return 0;

  const users: IUserBalance = await res.json();
  return Number(users.ref_balance) || 0;
};

export const getSupportMessages = async (userId: number, afterId: number = 0): Promise<SupportMessage[]> => {
  if (!Number.isFinite(userId) || userId <= 0) return [];
  const res = await fetch(`${apiUrl}/user/${userId}/support/messages?after_id=${afterId}`);
  return parseJsonOrThrow<SupportMessage[]>(res, "Не удалось загрузить переписку с поддержкой");
};

export const sendSupportMessage = async (userId: number, text: string, media?: File | null): Promise<SupportMessage> => {
  const body = new FormData();
  body.append("text", text);
  if (media) body.append("media", media);
  const res = await fetch(`${apiUrl}/user/${userId}/support/messages`, {
    method: "POST",
    body,
  });
  return parseJsonOrThrow<SupportMessage>(res, "Не удалось отправить сообщение");
};

export const getPartnerPercent = async (userId: number): Promise<number> => {
  const res = await fetch(`${apiUrl}/user/users/${userId}/partner_data`);
  const users = await res.json();
  if (!users) return 15
  return users.percentage
};

export const userWithdraw = async (
  userId: number,
  amount: number,
  wallet: string,
  walletType: string
): Promise<string> => {
  const res = await fetch(`${apiUrl}/user/withdraw?user_id=${userId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      amount,
      wallet,
      wallet_type: walletType,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    if (detail === "Insufficient balance") {
      throw new Error("Недостаточно средств на балансе");
    }
    if (typeof detail === "string") {
      throw new Error(detail);
    }
    throw new Error("Не удалось создать заявку на вывод");
  }

  return data?.message || "Заявка на вывод создана";
};

export const getUserPromo = async (userId: number): Promise<IPromocode[]> => {
  const res = await fetch(`${apiUrl}/user/users/${userId}/promo-codes`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const promocodes = await res.json();
  return promocodes;
};

export const checkPromoCode = async (promoCode: string, userId: number): Promise<IPromocode> => {
  const params = new URLSearchParams({ promo_code: promoCode.trim(), tg_id: String(userId) });
  const res = await fetch(`${apiUrl}/user/promo-codes/check?${params.toString()}`);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail;
    throw new Error(typeof detail === "string" ? detail : "Промокод не найден или недействителен");
  }
  return data as IPromocode;
};

export type MiniAppPaymentMethod = "cryptomus" | "tonconnect";

export type MiniAppPayment = {
  url: string;
  order_id: string;
  method: MiniAppPaymentMethod;
  currency: string;
  amount: number;
};

export type MiniAppManualPayment = {
  request_id: string;
  amount_usdt: number;
  amount_rub: number;
  wallet: string;
};

export const createMiniAppManualPayment = async (payload: {
  userId: number;
  amountUsdt: number;
  items: string[];
  tariffIds?: string[];
  duration: number | null;
  promoDiscount?: number;
}): Promise<MiniAppManualPayment> => {
  const res = await fetch(`${apiUrl}/user/mini-app/manual-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: payload.userId,
      amount_usdt: payload.amountUsdt,
      items: payload.items,
      tariff_ids: payload.tariffIds || [],
      duration: payload.duration,
      promo_discount: payload.promoDiscount || 0,
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : "Не удалось отправить заявку");
  return data as MiniAppManualPayment;
};

export const getMiniAppManualQuote = async (amountUsdt: number): Promise<{ amount_usdt: number; amount_rub: number; wallet: string }> => {
  const res = await fetch(`${apiUrl}/user/mini-app/manual-payment/quote?amount_usdt=${encodeURIComponent(amountUsdt)}`);
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : "Не удалось рассчитать сумму");
  return data;
};

export const createMiniAppPayment = async (
  userId: number,
  amount: number,
  method: MiniAppPaymentMethod,
  currency: "TON" | "VILARSO" = "TON"
): Promise<MiniAppPayment> => {
  const res = await fetch(`${apiUrl}/user/mini-app/payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, amount, method, currency }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail;
    throw new Error(typeof detail === "string" ? detail : "Не удалось создать ссылку на оплату");
  }
  return data as MiniAppPayment;
};

export const createUserPromo = async (
  userId: number,
  percent: number,
  name: string
): Promise<IPromocode> => {
  const res = await fetch(`${apiUrl}/user/promo-codes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      user_id: userId,
      percent,
    }),
  });
  const promocodes = await res.json();
  return promocodes;
};
